import { useClientAvatarUrl } from "@/hooks/use-client-avatar-url"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { avatarColor, avatarInitials } from "@/lib/avatar"
import { cn } from "@/lib/utils"

interface ClientAvatarProps {
  clientId:   string | null | undefined
  clientName: string | null | undefined
  /** Avatar size — defaults to "sm" */
  size?: "default" | "sm" | "lg"
  /** Extra className forwarded to the Avatar root */
  className?: string
}

/**
 * Displays a client avatar with presigned S3 image + color fallback.
 * Shows a tooltip with the client name on hover.
 * Renders "—" when clientName is absent.
 */
export function ClientAvatar({ clientId, clientName, size = "sm", className }: ClientAvatarProps) {
  const avatarUrl = useClientAvatarUrl(clientId)

  if (!clientName) return <span className="text-muted-foreground/30 text-xs">—</span>

  return (
    <Avatar size={size} className={cn("shrink-0", className)}>
      <AvatarImage src={avatarUrl ?? undefined} alt={clientName} />
      <AvatarFallback style={{ backgroundColor: avatarColor(clientName), color: "white" }}>
        {avatarInitials(clientName)}
      </AvatarFallback>
    </Avatar>
  )
}
